import React, { useMemo } from 'react';
import { Button, Input, Select, Space, Table, Tag, Popconfirm } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

export interface CreateBusinessFinancialData {
  id?: number;
  type?: string;
  bank?: string;
  branch_name?: string;
  bank_account?: string;
  _isDeletedLocal?: boolean;
  _fromDeleteRequest?: boolean;
  _isNewFromRequest?: boolean;
  _addRequest?: any;
  _deleteRequest?: any;
  _crIndex?: number;
  _actualIndex?: number;
}

interface FinancialTabProps {
  isEdit: boolean;
  isEditing?: boolean;
  isCustomerPortal?: boolean;
  financials: CreateBusinessFinancialData[];
  deletedFinancials?: Array<{ index: number; data: any }>;
  changeRequests?: Array<{ field_name: string; status: string; old_value?: string; new_value?: string; id: number }>;
  addFinancial: (financialData?: CreateBusinessFinancialData) => void;
  removeFinancial: (index: number) => void;
  updateFinancial: (index: number, field: keyof CreateBusinessFinancialData, value: string) => void;
  getFinancialDisplayValue?: (index: number, field: 'type' | 'bank' | 'branch_name' | 'bank_account') => string;
  handleFinancialFieldChange?: (index: number, field: 'type' | 'bank' | 'branch_name' | 'bank_account', value: string) => void;
  handleApproveChangeRequest?: (requestId: number) => void;
  handleRejectChangeRequest?: (requestId: number) => void;
  handleApproveFinancialDelete?: (requestId: number) => void;
  handleRejectFinancialDelete?: (requestId: number) => void;
}

const typeOptions = [
  { label: 'Current Account', value: 'Current Account' },
  { label: 'Savings Account', value: 'Savings Account' },
  { label: 'Fixed Deposit', value: 'Fixed Deposit' },
  { label: 'Other', value: 'Other' },
];

export const BusinessFinancialTab: React.FC<FinancialTabProps> = ({
  isEdit,
  isEditing = false,
  isCustomerPortal = false,
  financials,
  deletedFinancials = [],
  changeRequests = [],
  addFinancial,
  removeFinancial,
  updateFinancial,
  getFinancialDisplayValue,
  handleFinancialFieldChange,
  handleApproveChangeRequest,
  handleRejectChangeRequest,
  handleApproveFinancialDelete,
  handleRejectFinancialDelete,
}) => {
  // In create mode (!isEdit), always allow editing. In edit mode, use isEditing state.
  const canEdit = !isEdit || isEditing;
  const maxFinancials = 10;

  // Get change request for a specific field
  const getFieldChangeRequest = (index: number, field: 'type' | 'bank' | 'branch_name' | 'bank_account', financialId?: number) => {
    return changeRequests.find(cr => {
      if (cr.status !== 'pending') return false;
      // Match pattern: financial_{index}_{field}
      const pattern = `financial_${index}_${field}`;
      return cr.field_name === pattern;
    });
  };

  // Get delete change requests
  const getDeleteChangeRequest = (index: number) => {
    return changeRequests.find(cr => 
      cr.field_name === `financial_${index}_delete` && cr.status === 'pending'
    );
  };

  // Check if a financial is marked for deletion
  const isMarkedForDeletion = (index: number, id?: number, type?: string) => {
    // Check local deleted state
    const isLocallyDeleted = deletedFinancials.some(d => {
      if (id && d.data?.id) {
        return d.index === index && d.data.id === id;
      }
      return d.index === index;
    });

    // Check for pending delete change request
    const hasDeleteRequest = getDeleteChangeRequest(index) !== undefined;

    return isLocallyDeleted || hasDeleteRequest;
  };

  // Process financials with change requests
  const processedFinancials = useMemo(() => {
    const deletedFinancialKeys = new Set<string>();
    deletedFinancials.forEach(deleted => {
      if (deleted.data) {
        const key = `${deleted.data.id || ''}_${deleted.data.type || ''}_${deleted.data.bank || ''}_${deleted.data.bank_account || ''}`;
        deletedFinancialKeys.add(key);
      }
    });

    const allFinancials: Array<any> = [];
    
    financials.forEach((financial, currentIdx) => {
      const financialKey = `${financial.id || ''}_${financial.type || ''}_${financial.bank || ''}_${financial.bank_account || ''}`;
      if (!deletedFinancialKeys.has(financialKey)) {
        let originalIdx = currentIdx;
        deletedFinancials.forEach(deleted => {
          if (deleted.index <= currentIdx) {
            originalIdx++;
          }
        });
        allFinancials.push({ ...financial, _crIndex: originalIdx, _actualIndex: currentIdx });
      }
    });

    allFinancials.sort((a, b) => a._crIndex - b._crIndex);

    const sortedDeleted = [...deletedFinancials].sort((a, b) => b.index - a.index);
    sortedDeleted.forEach(deleted => {
      if (deleted.data) {
        const deletedKey = `${deleted.data.id || ''}_${deleted.data.type || ''}_${deleted.data.bank || ''}_${deleted.data.bank_account || ''}`;
        const exists = allFinancials.some(fin => {
          const finKey = `${fin.id || ''}_${fin.type || ''}_${fin.bank || ''}_${fin.bank_account || ''}`;
          return finKey === deletedKey;
        });
        if (!exists) {
          allFinancials.splice(deleted.index, 0, { ...deleted.data, _crIndex: deleted.index, _isDeletedLocal: true });
        }
      }
    });
    
    // Add new financials from change requests (pending add requests)
    // Track add request index for proper ordering
    let addRequestIndex = 0;
    changeRequests.forEach(cr => {
      if (cr.status === 'pending' && cr.field_name === 'financial_add') {
        // Handle financial_add format: contains a single financial object (not an array)
        try {
          const newFinancial = JSON.parse(cr.new_value || '{}');
          const newFinancialKey = `${newFinancial.id || ''}_${newFinancial.type || ''}_${newFinancial.bank || ''}_${newFinancial.bank_account || ''}`;
          if (!newFinancialKey.trim()) return;

          const existsInAllFinancials = allFinancials.some(fin => {
            const finKey = `${fin.id || ''}_${fin.type || ''}_${fin.bank || ''}_${fin.bank_account || ''}`;
            return finKey === newFinancialKey && finKey.trim() !== '';
          });

          if (!existsInAllFinancials) {
            const placeholder = { ...newFinancial, _isNewFromRequest: true, _addRequest: cr, _crIndex: 999 + addRequestIndex };
            allFinancials.push(placeholder as any);
            addRequestIndex++;
          }
        } catch (e) {
          console.error('FinancialTab: Error parsing financial_add request:', e);
        }
      } else if (cr.status === 'pending' && cr.field_name.endsWith('_add')) {
        const parts = cr.field_name.split('_');
        if (parts.length >= 3 && parts[0] === 'financial') {
          const index = parseInt(parts[1], 10);
          try {
            const newFinancial = JSON.parse(cr.new_value || '{}');
            const newFinancialKey = `${newFinancial.id || ''}_${newFinancial.type || ''}_${newFinancial.bank || ''}_${newFinancial.bank_account || ''}`;
            
            if (!newFinancialKey.trim()) {
              return;
            }
            
            const existsInAllFinancials = allFinancials.some(fin => {
              const finKey = `${fin.id || ''}_${fin.type || ''}_${fin.bank || ''}_${fin.bank_account || ''}`;
              return finKey === newFinancialKey && finKey.trim() !== '';
            });
            
            if (!existsInAllFinancials) {
              const placeholder = { ...newFinancial, _isNewFromRequest: true, _addRequest: cr, _crIndex: index };
              allFinancials.push(placeholder as any);
            }
          } catch (e) {
            console.error('FinancialTab: Error parsing financial_{index}_add request:', e);
          }
        }
      }
    });

    const financialData = allFinancials.map((fin, idx) => {
      const crIdx = fin._crIndex !== undefined ? fin._crIndex : idx;
      const actualIdx = fin._actualIndex !== undefined ? fin._actualIndex : idx;
      const financialId = fin.id;
      const financialType = fin.type || '';
      const financialBank = fin.bank || '';
      const financialAccount = fin.bank_account || '';
      const isDeleted = fin._isDeletedLocal || 
                       deletedFinancials.some(d => {
                         if (d.index === crIdx && d.data) {
                           const deletedKey = `${d.data.id || ''}_${d.data.type || ''}_${d.data.bank || ''}_${d.data.bank_account || ''}`;
                           const finKey = `${financialId || ''}_${financialType}_${financialBank}_${financialAccount}`;
                           return deletedKey === finKey;
                         }
                         return false;
                       }) ||
                       getDeleteChangeRequest(crIdx) !== undefined;
      const deleteReq = getDeleteChangeRequest(crIdx);
      return {
        ...fin,
        key: `financial-${crIdx}-${idx}-${fin.id || 'new'}-${fin._isNewFromRequest ? 'new-request' : ''}`,
        originalIndex: crIdx,
        actualIndex: actualIdx,
        isDeleted: isDeleted,
        deleteRequest: deleteReq,
        isNewFromRequest: fin._isNewFromRequest || false,
        addRequest: fin._addRequest,
      };
    });

    // Sort financials: deleted (0) < pending add from request (1) < local new row (2) < existing (0)
    // Reference: AddressTab.tsx getRowWeight and sort logic
    const getRowWeight = (record: any) => {
      if (record.isDeleted) return 0;
      if (record.isNewFromRequest) return 1; // pending add from backend
      if (!record.id) return 2; // local new row (not yet requested)
      return 0; // existing row
    };

    financialData.sort((a: any, b: any) => {
      const wA = getRowWeight(a);
      const wB = getRowWeight(b);
      if (wA !== wB) return wA - wB;
      const idxA = a.originalIndex;
      const idxB = b.originalIndex;
      return idxA - idxB;
    });
    
    return financialData;
  }, [financials, deletedFinancials, changeRequests]);

  const hasPendingFieldChange = (index: number) => {
    return changeRequests.some(
      (cr) =>
        cr.status === 'pending' &&
        cr.field_name.startsWith(`financial_${index}_`) &&
        !cr.field_name.endsWith('_add') &&
        !cr.field_name.endsWith('_delete') &&
        cr.field_name !== 'financial_add'
    );
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: '25%',
      render: (type: string, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const typeChangeRequest = getFieldChangeRequest(record.originalIndex, 'type', record.id);
        
        if (canEdit && !record.isDeleted) {
          let displayValue = type || '';
          if (typeChangeRequest && typeChangeRequest.new_value) {
            displayValue = typeChangeRequest.new_value;
          } else if (record.isNewFromRequest && record.addRequest) {
            try {
              const parsed = JSON.parse(record.addRequest.new_value || '{}');
              const newFin = Array.isArray(parsed) ? parsed.find((f: any) => f.type === type && f.bank === record.bank) || parsed[0] : parsed;
              displayValue = newFin?.type || type || '';
            } catch (e) {
              displayValue = type || '';
            }
          } else if (getFinancialDisplayValue) {
            const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
            const pending = getFinancialDisplayValue(indexToUse, 'type');
            if (pending !== '') displayValue = pending;
          }
          const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
          return (
          <Select
              id={`financial-type-${indexToUse}`}
              value={displayValue}
              placeholder="Select type"
            style={{ width: '100%' }}
              onChange={(val) => {
                if (handleFinancialFieldChange) {
                  handleFinancialFieldChange(indexToUse, 'type', val);
                } else {
                  updateFinancial(indexToUse, 'type', val);
                }
              }}
              options={typeOptions}
            />
          );
        }

        if (typeChangeRequest) {
          if (typeChangeRequest.status === 'approved') {
            return <span>{typeChangeRequest.new_value || type || '-'}</span>;
          }
          if (typeChangeRequest.status === 'rejected') {
            return <span>{typeChangeRequest.old_value || type || '-'}</span>;
          }
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
                {typeChangeRequest.old_value || type || '-'}
              </span>
              <span style={{ color: '#ff9800' }}>
                {typeChangeRequest.new_value || '-'}
              </span>
            </div>
          );
        }

        return <span style={cellStyle}>{type || '-'}</span>;
      },
    },
    {
      title: 'Bank',
      dataIndex: 'bank',
      key: 'bank',
      width: '25%',
      render: (bank: string, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const bankChangeRequest = getFieldChangeRequest(record.originalIndex, 'bank', record.id);
        
        if (canEdit && !record.isDeleted) {
          let displayValue = bank || '';
          if (bankChangeRequest && bankChangeRequest.new_value) {
            displayValue = bankChangeRequest.new_value;
          } else if (record.isNewFromRequest && record.addRequest) {
            try {
              const parsed = JSON.parse(record.addRequest.new_value || '{}');
              const newFin = Array.isArray(parsed) ? parsed.find((f: any) => f.type === record.type && f.bank === bank) || parsed[0] : parsed;
              displayValue = newFin?.bank || bank || '';
            } catch (e) {
              displayValue = bank || '';
            }
          } else if (getFinancialDisplayValue) {
            const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
            const pending = getFinancialDisplayValue(indexToUse, 'bank');
            if (pending !== '') displayValue = pending;
          }
          const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
          return (
                <Input
              id={`financial-bank-${indexToUse}`}
              name={`financial-bank-${indexToUse}`}
              value={displayValue}
              onChange={(e) => {
                const val = e.target.value;
                if (handleFinancialFieldChange) {
                  handleFinancialFieldChange(indexToUse, 'bank', val);
                } else {
                  updateFinancial(indexToUse, 'bank', val);
                }
              }}
              placeholder="Bank"
            />
          );
        }

        if (bankChangeRequest) {
          if (bankChangeRequest.status === 'approved') {
            return <span>{bankChangeRequest.new_value || bank || '-'}</span>;
          }
          if (bankChangeRequest.status === 'rejected') {
            return <span>{bankChangeRequest.old_value || bank || '-'}</span>;
          }
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
                {bankChangeRequest.old_value || bank || '-'}
              </span>
              <span style={{ color: '#ff9800' }}>
                {bankChangeRequest.new_value || '-'}
              </span>
            </div>
          );
        }

        return <span style={cellStyle}>{bank || '-'}</span>;
      },
    },
    {
      title: 'Branch',
      dataIndex: 'branch_name',
      key: 'branch_name',
      width: '25%',
      render: (branch_name: string, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const branchChangeRequest = getFieldChangeRequest(record.originalIndex, 'branch_name', record.id);
        
        if (canEdit && !record.isDeleted) {
          let displayValue = branch_name || '';
          if (branchChangeRequest && branchChangeRequest.new_value) {
            displayValue = branchChangeRequest.new_value;
          } else if (record.isNewFromRequest && record.addRequest) {
            try {
              const parsed = JSON.parse(record.addRequest.new_value || '{}');
              const newFin = Array.isArray(parsed) ? parsed.find((f: any) => f.type === record.type && f.bank === record.bank) || parsed[0] : parsed;
              displayValue = newFin?.branch_name || branch_name || '';
            } catch (e) {
              displayValue = branch_name || '';
            }
          } else if (getFinancialDisplayValue) {
            const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
            const pending = getFinancialDisplayValue(indexToUse, 'branch_name');
            if (pending !== '') displayValue = pending;
          }
          const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
          return (
                <Input
              id={`financial-branch-${indexToUse}`}
              name={`financial-branch-${indexToUse}`}
              value={displayValue}
              onChange={(e) => {
                const val = e.target.value;
                if (handleFinancialFieldChange) {
                  handleFinancialFieldChange(indexToUse, 'branch_name', val);
                } else {
                  updateFinancial(indexToUse, 'branch_name', val);
                }
              }}
              placeholder="Branch"
            />
          );
        }

        if (branchChangeRequest) {
          if (branchChangeRequest.status === 'approved') {
            return <span>{branchChangeRequest.new_value || branch_name || '-'}</span>;
          }
          if (branchChangeRequest.status === 'rejected') {
            return <span>{branchChangeRequest.old_value || branch_name || '-'}</span>;
          }
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
                {branchChangeRequest.old_value || branch_name || '-'}
              </span>
              <span style={{ color: '#ff9800' }}>
                {branchChangeRequest.new_value || '-'}
              </span>
            </div>
          );
        }

        return <span style={cellStyle}>{branch_name || '-'}</span>;
      },
    },
    {
      title: 'Account No.',
      dataIndex: 'bank_account',
      key: 'bank_account',
      width: '15%',
      render: (bank_account: string, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const accountChangeRequest = getFieldChangeRequest(record.originalIndex, 'bank_account', record.id);
        
        if (canEdit && !record.isDeleted) {
          let displayValue = bank_account || '';
          if (accountChangeRequest && accountChangeRequest.new_value) {
            displayValue = accountChangeRequest.new_value;
          } else if (record.isNewFromRequest && record.addRequest) {
            try {
              const parsed = JSON.parse(record.addRequest.new_value || '{}');
              const newFin = Array.isArray(parsed) ? parsed.find((f: any) => f.type === record.type && f.bank === record.bank) || parsed[0] : parsed;
              displayValue = newFin?.bank_account || bank_account || '';
            } catch (e) {
              displayValue = bank_account || '';
            }
          } else if (getFinancialDisplayValue) {
            const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
            const pending = getFinancialDisplayValue(indexToUse, 'bank_account');
            if (pending !== '') displayValue = pending;
          }
          const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
          return (
          <Input
              id={`financial-account-${indexToUse}`}
              name={`financial-account-${indexToUse}`}
              value={displayValue}
              onChange={(e) => {
                const val = e.target.value;
                if (handleFinancialFieldChange) {
                  handleFinancialFieldChange(indexToUse, 'bank_account', val);
                } else {
                  updateFinancial(indexToUse, 'bank_account', val);
                }
              }}
              placeholder="Account No."
            />
          );
        }

        if (accountChangeRequest) {
          if (accountChangeRequest.status === 'approved') {
            return <span>{accountChangeRequest.new_value || bank_account || '-'}</span>;
          }
          if (accountChangeRequest.status === 'rejected') {
            return <span>{accountChangeRequest.old_value || bank_account || '-'}</span>;
          }
  return (
    <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
                {accountChangeRequest.old_value || bank_account || '-'}
              </span>
              <span style={{ color: '#ff9800' }}>
                {accountChangeRequest.new_value || '-'}
              </span>
            </div>
          );
        }

        return <span style={cellStyle}>{bank_account || '-'}</span>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: '10%',
      render: (_: any, record: any) => {
        // Handle deleted financials
        if (record.isDeleted || record.deleteRequest) {
          if (!isEditing) return null;
          // Customer portal: show pending status
          if (isCustomerPortal) {
            return <Tag color="orange">Pending</Tag>;
          }
          // Admin portal: show approve/reject buttons
          const deleteReq = record.deleteRequest || changeRequests.find(cr => 
            cr.field_name === `financial_${record.originalIndex}_delete` && cr.status === 'pending'
          );
          if (deleteReq) {
            return (
              <Space>
                <Button
                  type="primary"
                  size="small"
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                  onClick={() => handleApproveFinancialDelete && handleApproveFinancialDelete(deleteReq.id)}
                >
                  Approve
                </Button>
      <Button
                  danger
                  size="small"
                  onClick={() => handleRejectFinancialDelete && handleRejectFinancialDelete(deleteReq.id)}
                >
                  Reject
                </Button>
              </Space>
            );
          }
        }

        // Handle new financial from change request (add request)
        if (record.isNewFromRequest && record.addRequest) {
          if (!isEditing) return null;
          // Customer portal: show pending status
          if (isCustomerPortal) {
            return <Tag color="orange">Pending</Tag>;
          }
          // Admin portal: show approve/reject buttons
          return (
            <Space>
              <Button
                type="primary"
                size="small"
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                onClick={() => handleApproveChangeRequest && handleApproveChangeRequest(record.addRequest.id)}
              >
                Approve
              </Button>
              <Button
                danger
                size="small"
                onClick={() => handleRejectChangeRequest && handleRejectChangeRequest(record.addRequest.id)}
              >
                Reject
              </Button>
            </Space>
          );
        }

        // Handle field-level change requests
        const fieldChangeRequests = [
          getFieldChangeRequest(record.originalIndex, 'type', record.id),
          getFieldChangeRequest(record.originalIndex, 'bank', record.id),
          getFieldChangeRequest(record.originalIndex, 'branch_name', record.id),
          getFieldChangeRequest(record.originalIndex, 'bank_account', record.id),
        ].filter(Boolean);

        if (fieldChangeRequests.length > 0) {
          if (!isEditing) return null;
          // Customer portal: show pending status
          if (isCustomerPortal) {
            return <Tag color="orange">Pending</Tag>;
          }
          // Admin portal: show approve/reject buttons
          return (
            <Space>
              {fieldChangeRequests.map((cr, idx) => (
                <React.Fragment key={idx}>
                  <Button
                    type="primary"
                    size="small"
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                    onClick={() => handleApproveChangeRequest && handleApproveChangeRequest(cr!.id)}
                  >
                    Approve
                  </Button>
                  <Button
                    danger
                    size="small"
                    onClick={() => handleRejectChangeRequest && handleRejectChangeRequest(cr!.id)}
                  >
                    Reject
                  </Button>
                </React.Fragment>
              ))}
            </Space>
          );
        }

        // Delete button: show for existing rows (with id) and new local rows (without id and not from request)
        // This should work for both customer portal and admin portal
        if (canEdit && !record.isDeleted && !record.isNewFromRequest) {
          const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
          return (
            <Popconfirm
              title="Are you sure you want to delete this financial?"
              onConfirm={() => removeFinancial(indexToUse)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
              >
                Delete
              </Button>
            </Popconfirm>
          );
        }
        return null;
      },
    },
  ];

  return (
    <div>
      <Table
        columns={columns}
        dataSource={processedFinancials}
        pagination={false}
        rowKey="key"
        onRow={(record: any) => {
          const isDeleted = record.isDeleted;
          const isNew = record.isNewFromRequest;
          const hasPendingFieldChange = changeRequests.some(
            (cr) =>
              cr.status === 'pending' &&
              cr.field_name.startsWith(`financial_${record.originalIndex}_`) &&
              !cr.field_name.endsWith('_add') &&
              !cr.field_name.endsWith('_delete') &&
              cr.field_name !== 'financial_add'
          );
          return {
            style: {
              backgroundColor: isDeleted ? '#ffebee' : isNew ? '#e3f2fd' : hasPendingFieldChange ? '#fff3cd' : undefined,
            },
          };
        }}
      />
      {canEdit && processedFinancials.filter((f: any) => !f.isDeleted && !f.isNewFromRequest).length < maxFinancials && (
        <div style={{ marginTop: 12 }}>
          <Button
            type="link"
            icon={<PlusOutlined />}
            style={{ color: '#ff4d4f', padding: 0 }}
            onClick={() => addFinancial({ type: '', bank: '', branch_name: '', bank_account: '' })}
          >
            (max {maxFinancials})
      </Button>
        </div>
      )}
    </div>
  );
};
